import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spin } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  LockOutlined,
  TrophyOutlined,
} from '@ant-design/icons';

import PageWrapper from '../../components/MainPageWrapper/PageWrapper';
import { APP_URL, ENDPOINT } from '../../constants/common-app.const';
import { CourseTopic, CourseTopicStatus } from './Course.const';
import useGetContractWithProgress from '../../hooks/useGetContractWithProgress';
import useGetThemeContent from '../../hooks/useGetThemeContent';
import api from '../../utils/api.util';
import toast from '../../utils/toast.util';

import css from './Course.module.css';

// Recognised inline tokens: images, <br>, and theme-links [[theme:<uuid>|text]].
// Raw HTML is never injected — each token becomes a controlled React element.
const INLINE_TOKEN_REGEX =
  /!\[([^\]]*)\]\(([^)\s]+)\)|<br\s*\/?>|\[\[theme:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\|([^\]]+)\]\]/gi;
const HEADING_REGEX = /^(#{1,6})\s+(.+)$/;

const isSafeImageUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const renderInline = (
  text: string,
  keyPrefix: string,
  openThemeIds: Set<string>,
  onThemeLink: (themeId: string) => void
): JSX.Element[] => {
  const nodes: JSX.Element[] = [];
  let lastIndex = 0;
  let partIdx = 0;
  let match: RegExpExecArray | null;
  INLINE_TOKEN_REGEX.lastIndex = 0;
  while ((match = INLINE_TOKEN_REGEX.exec(text)) !== null) {
    const full = match[0];
    const before = text.slice(lastIndex, match.index);
    if (before) nodes.push(<span key={`${keyPrefix}t${partIdx++}`}>{before}</span>);
    if (full[0] === '!') {
      const alt = match[1];
      const url = match[2];
      if (isSafeImageUrl(url)) {
        nodes.push(
          <img key={`${keyPrefix}i${partIdx++}`} src={url} alt={alt} className={css.ContentImage} />
        );
      } else {
        nodes.push(<span key={`${keyPrefix}t${partIdx++}`}>{full}</span>);
      }
    } else if (full[0] === '[') {
      const themeId = match[3];
      const linkText = match[4];
      if (openThemeIds.has(themeId)) {
        nodes.push(
          <button
            key={`${keyPrefix}l${partIdx++}`}
            type='button'
            className={css.ContentLink}
            onClick={() => onThemeLink(themeId)}
          >
            {linkText}
          </button>
        );
      } else {
        // Target is not a known open theme — render link text only, never a link.
        nodes.push(<span key={`${keyPrefix}t${partIdx++}`}>{linkText}</span>);
      }
    } else {
      nodes.push(<br key={`${keyPrefix}b${partIdx++}`} />);
    }
    lastIndex = match.index + full.length;
  }
  const tail = text.slice(lastIndex);
  if (tail) nodes.push(<span key={`${keyPrefix}t${partIdx++}`}>{tail}</span>);
  return nodes;
};

const renderCell = (
  text: string,
  keyPrefix: string,
  openThemeIds: Set<string>,
  onThemeLink: (themeId: string) => void
): JSX.Element[] | string => {
  const nodes = renderInline(text, keyPrefix, openThemeIds, onThemeLink);
  return nodes.length ? nodes : text;
};

// Split a GFM table row into trimmed cells, dropping the outer pipes.
const splitTableRow = (line: string): string[] => {
  let l = line.trim();
  if (l.startsWith('|')) l = l.slice(1);
  if (l.endsWith('|')) l = l.slice(0, -1);
  return l.split('|').map(c => c.trim());
};

// Detect a GFM table: a header row, a delimiter row (---, :--, --:, :--:),
// then zero or more body rows. Returns null when the segment isn't a table.
const parseTable = (
  seg: string
): { header: string[]; rows: string[][] } | null => {
  const lines = seg.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2 || !lines[0].includes('|')) return null;
  const delim = splitTableRow(lines[1]);
  if (delim.length === 0 || !delim.every(c => /^:?-+:?$/.test(c))) return null;
  return {
    header: splitTableRow(lines[0]),
    rows: lines.slice(2).map(splitTableRow),
  };
};

const renderContentSegment = (
  seg: string,
  segIdx: number,
  openThemeIds: Set<string>,
  onThemeLink: (themeId: string) => void
): JSX.Element[] => {
  const table = parseTable(seg);
  if (table) {
    return [
      <table key={`tbl${segIdx}`} className={css.ContentTable}>
        <thead>
          <tr>
            {table.header.map((cell, i) => (
              <th key={i}>{renderCell(cell, `h${segIdx}-${i}-`, openThemeIds, onThemeLink)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c}>{renderCell(cell, `c${segIdx}-${r}-${c}-`, openThemeIds, onThemeLink)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>,
    ];
  }

  // A single \n\n-delimited segment may interleave headings with prose, so we
  // split on single newlines and emit a heading element per heading line while
  // grouping consecutive prose lines into one paragraph (joined as authored,
  // with <br> tokens providing explicit breaks).
  const blocks: JSX.Element[] = [];
  let para: string[] = [];
  let listItems: string[] = [];
  const flushPara = () => {
    if (!para.length) return;
    const text = para.join('\n');
    const key = `p${segIdx}-${blocks.length}`;
    const nodes = renderInline(text, `${key}-`, openThemeIds, onThemeLink);
    blocks.push(
      <p key={key} className={css.ContentParagraph}>
        {nodes.length ? nodes : text}
      </p>
    );
    para = [];
  };
  const flushList = () => {
    if (!listItems.length) return;
    const key = `ul${segIdx}-${blocks.length}`;
    blocks.push(
      <ul key={key} className={css.ContentList}>
        {listItems.map((item, i) => {
          const nodes = renderInline(item, `${key}-${i}-`, openThemeIds, onThemeLink);
          return <li key={i}>{nodes.length ? nodes : item}</li>;
        })}
      </ul>
    );
    listItems = [];
  };

  seg.split('\n').forEach(line => {
    const trimmed = line.trim();
    // Bullet list item: "- text" or "* text" (the marker must be followed by
    // whitespace, so a "---" rule or "-1" is not mistaken for a list).
    const listItem = /^[-*]\s+(.*)$/.exec(trimmed);
    if (listItem) {
      flushPara();
      listItems.push(listItem[1]);
      return;
    }
    const heading = HEADING_REGEX.exec(trimmed);
    if (heading) {
      flushPara();
      flushList();
      const level = Math.min(heading[1].length + 1, 6);
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      const key = `h${segIdx}-${blocks.length}`;
      const nodes = renderInline(heading[2], `${key}-`, openThemeIds, onThemeLink);
      blocks.push(
        <Tag key={key} className={css.ContentHeading}>
          {nodes.length ? nodes : heading[2]}
        </Tag>
      );
      return;
    }
    flushList();
    para.push(line);
  });
  flushPara();
  flushList();

  return blocks;
};

const deriveTopicStatus = (
  themeId: string,
  sortedThemes: CourseTopic[],
  completedThemeIds: Set<string>,
  isOpen: boolean
): CourseTopicStatus => {
  if (completedThemeIds.has(themeId)) return 'completed';
  if (isOpen) return 'current'; // open themes are never locked
  const firstIncomplete = sortedThemes.find(
    t => !t.isOpen && !completedThemeIds.has(t.id)
  );
  if (firstIncomplete?.id === themeId) return 'current';
  return 'locked';
};

const Course = () => {
  const history = useHistory();
  const { id: contractId } = useParams<{ id: string }>();

  const {
    contract,
    themes,
    listenerUid,
    completedThemeIds,
    markThemeCompleted,
    loading,
  } = useGetContractWithProgress({ contractId });

  const sortedThemes = useMemo(
    () => [...themes].sort((a, b) => a.order - b.order),
    [themes]
  );
  const topics: CourseTopic[] = useMemo(
    () =>
      sortedThemes.map(t => ({
        ...t,
        status: deriveTopicStatus(t.id, sortedThemes, completedThemeIds, t.isOpen),
      })),
    [sortedThemes, completedThemeIds]
  );

  const openThemeIds = useMemo(
    () => new Set(topics.filter(t => t.isOpen).map(t => t.id)),
    [topics]
  );

  const firstUnlocked = topics.find(t => t.status !== 'locked') ?? topics[0];
  const [navStack, setNavStack] = useState<string[]>([]);
  const [showCompletion, setShowCompletion] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const resolvedActiveId =
    navStack[navStack.length - 1] ?? firstUnlocked?.id ?? null;

  const selectTopic = (themeId: string) => {
    setShowCompletion(false);
    setNavStack([themeId]);
  };
  const isLinkView = navStack.length > 1;
  const goBack = () => setNavStack(prev => prev.slice(0, -1));

  const { body, loading: contentLoading } = useGetThemeContent({
    contractId,
    themeId: resolvedActiveId ?? '',
  });

  const autoCompletedRef = useRef<Set<string>>(new Set());
  const activeTopicRef = useRef<HTMLLIElement | null>(null);

  // Keep the active theme visible in the sidebar as the user moves through the
  // course (Next/Prev, inline links, auto-advance).
  useEffect(() => {
    activeTopicRef.current?.scrollIntoView({ block: 'nearest' });
  }, [resolvedActiveId]);

  useEffect(() => {
    const active = topics.find(t => t.id === resolvedActiveId);
    if (
      !active || !active.isOpen || !listenerUid ||
      completedThemeIds.has(active.id) ||
      autoCompletedRef.current.has(active.id)
    ) {
      return;
    }
    autoCompletedRef.current.add(active.id);
    api
      .post(
        `${ENDPOINT.CONTRACTS}/${contractId}/listeners/${listenerUid}/themes/${active.id}/complete`,
        {}
      )
      .then(() => markThemeCompleted(active.id))
      .catch(() => {
        autoCompletedRef.current.delete(active.id);
        toast.showOnFailure('Ошибка отметки темы');
      });
  }, [resolvedActiveId, topics, listenerUid, completedThemeIds, contractId, markThemeCompleted]);

  const completedCount = topics.filter(t => completedThemeIds.has(t.id)).length;
  const total = topics.length;
  const progress = total > 0 ? (completedCount / total) * 100 : 0;

  const activeIndex = topics.findIndex(t => t.id === resolvedActiveId);
  const activeTopic = topics[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === total - 1;

  const handleThemeLink = (themeId: string) => {
    setShowCompletion(false);
    setNavStack(prev => [...prev, themeId]);
  };

  const goPrev = () => {
    if (!isFirst) selectTopic(topics[activeIndex - 1].id);
  };

  const goNext = async () => {
    if (!activeTopic || !listenerUid) return;
    setSubmitting(true);
    try {
      await api.post(
        `${ENDPOINT.CONTRACTS}/${contractId}/listeners/${listenerUid}/themes/${activeTopic.id}/complete`,
        {}
      );
      markThemeCompleted(activeTopic.id);
      if (isLast) {
        setShowCompletion(true);
      } else {
        selectTopic(topics[activeIndex + 1].id);
      }
    } catch (err: any) {
      const status = err?.statusCode ?? err?.status;
      if (status === 422) {
        toast.showOnFailure('Необходимо пройти предыдущие темы последовательно');
      } else if (status === 402) {
        toast.showOnFailure('Курс недоступен — договор не оплачен');
      } else {
        toast.showOnFailure('Ошибка подтверждения темы');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper hideLeftPanel>
        <div className={css.Layout}>
          <Spin />
        </div>
      </PageWrapper>
    );
  }

  if (!topics.length) {
    return (
      <PageWrapper hideLeftPanel>
        <div className={css.Layout}>
          <aside className={css.Sidebar}>
            <button
              type='button'
              className={css.BackBtn}
              onClick={() => history.push(APP_URL.MY_COURSES)}
            >
              <ArrowLeftOutlined /> К моим курсам
            </button>
            <div className={css.SidebarHeader}>
              <h2 className={css.SidebarTitle}>Содержание курса</h2>
            </div>
            <p style={{ padding: '16px' }}>Содержание курса не добавлено</p>
          </aside>
          <section className={css.Main}>
            <header className={css.MainHeader}>
              <div className={css.MainHeaderTop}>
                <h1 className={css.CourseTitle}>{contract?.course?.name ?? ''}</h1>
              </div>
            </header>
          </section>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper hideLeftPanel>
      <div className={css.Layout}>
        <aside className={css.Sidebar}>
          <button
            type='button'
            className={css.BackBtn}
            onClick={() => history.push(APP_URL.MY_COURSES)}
          >
            <ArrowLeftOutlined /> К моим курсам
          </button>
          <div className={css.SidebarHeader}>
            <h2 className={css.SidebarTitle}>Содержание курса</h2>
            <p className={css.SidebarProgressText}>
              {completedCount} из {total} тем пройдено
            </p>
            <div className={css.ProgressTrack}>
              <div
                className={css.ProgressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <ul className={css.TopicList}>
            {topics.map((topic) => {
              const isActive = topic.id === resolvedActiveId;
              return (
                <li
                  key={topic.id}
                  ref={isActive ? activeTopicRef : undefined}
                  className={`${css.TopicItem} ${isActive ? css.TopicItemActive : ''}`}
                  onClick={() => {
                    if (topic.status !== 'locked') selectTopic(topic.id);
                  }}
                >
                  <span
                    className={`${css.TopicNumber} ${isActive ? css.TopicNumberActive : ''}`}
                  >
                    {topic.order}
                  </span>
                  <div className={css.TopicInfo}>
                    <div className={css.TopicTitle}>{topic.title}</div>
                    <div className={css.TopicMeta}>—</div>
                  </div>
                  <span className={css.TopicStatus}>
                    {topic.status === 'completed' && (
                      <CheckCircleFilled className={css.IconCompleted} />
                    )}
                    {topic.status === 'locked' && (
                      <LockOutlined className={css.IconLocked} />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className={css.Main}>
          <header className={css.MainHeader}>
            <div className={css.MainHeaderTop}>
              <h1 className={css.CourseTitle}>{contract?.course?.name ?? ''}</h1>
              <span className={css.StatusBadge}>
                <ClockCircleOutlined /> {contract?.status ?? ''}
              </span>
            </div>
          </header>

          {showCompletion && (
            <div className={css.CompletionScreen}>
              <TrophyOutlined className={css.CompletionIcon} />
              <h2 className={css.CompletionTitle}>Вы успешно закончили обучение!</h2>
              <p className={css.CompletionText}>
                Вам будет выдан сертификат в ближайшее время.
              </p>
              <div className={css.CompletionActions}>
                <Button
                  className={css.CompletionBtn}
                  onClick={() => {
                    setNavStack(topics[0] ? [topics[0].id] : []);
                    setShowCompletion(false);
                  }}
                >
                  Вернуться к темам
                </Button>
                <Button
                  type='primary'
                  className={css.CompletionBtn}
                  onClick={() => history.push(APP_URL.MY_COURSES)}
                >
                  К моим курсам
                </Button>
              </div>
            </div>
          )}

          {!showCompletion && activeTopic && (
            <>
              <div className={css.TopicHeader}>
                <div className={css.TopicHeaderMeta}>
                  Тема {activeTopic.order} из {total}
                </div>
                <h2 className={css.TopicHeaderTitle}>{activeTopic.title}</h2>
              </div>

              {isLinkView && (
                <button
                  type='button'
                  className={css.ContentBackBtn}
                  onClick={goBack}
                >
                  <ArrowLeftOutlined aria-hidden="true" /> Назад
                </button>
              )}

              <div className={css.Content}>
                {contentLoading ? (
                  <Spin />
                ) : (
                  (body ?? '').split('\n\n').flatMap((seg, idx) =>
                    renderContentSegment(seg, idx, openThemeIds, handleThemeLink)
                  )
                )}
              </div>

              {!isLinkView && (
                <div className={css.Footer}>
                  <Button
                    className={css.NavBtn}
                    icon={<ArrowLeftOutlined />}
                    disabled={isFirst || submitting}
                    onClick={goPrev}
                  >
                    Назад
                  </Button>
                  <div className={css.FooterRight}>
                    <Button
                      type='primary'
                      className={css.NextBtn}
                      onClick={goNext}
                      loading={submitting}
                      disabled={!listenerUid || submitting}
                    >
                      {isLast ? 'Завершить курс' : 'Следующая'}
                      {!isLast && !submitting && <ArrowRightOutlined />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </PageWrapper>
  );
};

export default Course;
